import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { AlertTriangle, Info } from 'lucide-react';
import Modal from './Modal';
import {
  usersApi,
  UserDto,
  RoleChangeImpact,
  RoleChangeAffectedCategory,
  RoleChangeLeadHandling,
  RoleChangeClientHandling,
  SecretaryConversionMode,
  ChangePermissionLevelPayload,
} from '../services/usersService';

export interface RoleChangeData {
  userId: number;
  userName: string;
  oldLevel: number;
  newLevel: number;
  isSupervisor: boolean;
}

interface RoleChangeModalProps {
  data: RoleChangeData | null;
  onClose: () => void;
  onSuccess: (message: string) => void;
}

const getRoleName = (level: number): string => {
  switch (level) {
    case 1: return 'מנהל מערכת';
    case 2: return 'נציג מכירות';
    case 3: return 'מנהל תיקים';
    case 4: return 'מזכירה';
    default: return 'לא ידוע';
  }
};

const Summary = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 12px 16px;
  background: var(--hover-background, #f5f6f8);
  border-radius: var(--border-radius-sm);
  margin-bottom: 16px;
  font-size: 14px;
  color: var(--text-primary);
`;

const RoleBadge = styled.span<{ $variant: 'old' | 'new' }>`
  font-weight: 700;
  color: ${props => (props.$variant === 'new' ? 'var(--primary-color)' : 'var(--text-secondary)')};
`;

const SectionTitle = styled.p`
  margin: 0 0 10px 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;

const Options = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
`;

const OptionLabel = styled.label<{ $checked: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 12px;
  border: 1px solid ${props => (props.$checked ? 'var(--primary-color)' : 'var(--border-color)')};
  border-radius: var(--border-radius-sm);
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  background: ${props => (props.$checked ? 'var(--primary-light, rgba(0,115,234,0.06))' : 'var(--surface-color)')};

  &:hover {
    border-color: var(--primary-color);
  }

  input {
    margin-top: 3px;
    flex-shrink: 0;
  }
`;

const OptionText = styled.span`
  font-size: 13.5px;
  color: var(--text-primary);
  line-height: 1.5;
`;

const Select = styled.select`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  color: var(--text-primary);
  background: var(--surface-color);
  margin: 6px 0 4px 0;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }

  &.error {
    border-color: var(--error-color);
  }
`;

const NoteBox = styled.div<{ $type?: 'info' | 'warning' }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  border-radius: var(--border-radius-sm);
  font-size: 13px;
  line-height: 1.5;
  margin-top: 12px;
  color: ${props => (props.$type === 'warning' ? '#92400e' : 'var(--text-secondary)')};
  background: ${props => (props.$type === 'warning' ? '#fef3c7' : 'var(--hover-background, #f5f6f8)')};

  svg { flex-shrink: 0; margin-top: 1px; }
`;

const InlineError = styled.div`
  color: var(--error-color, #dc2626);
  font-size: 13px;
  margin-top: 10px;
`;

const Centered = styled.div`
  text-align: center;
  color: var(--text-secondary);
  padding: 24px 0;
  font-size: 14px;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 22px;
  border: none;
  border-radius: var(--border-radius-sm);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 100px;

  &:disabled { opacity: 0.6; cursor: not-allowed; }

  ${props => props.variant === 'primary'
    ? `
      background: var(--primary-color);
      color: white;
      border: 1px solid var(--primary-color);
      &:hover:not(:disabled) { background: transparent; color: var(--primary-color); }
    `
    : `
      background: var(--surface-color);
      color: var(--text-primary);
      border: 1px solid var(--border-color);
      &:hover:not(:disabled) { background: var(--hover-background); }
    `}
`;

const RoleChangeModal: React.FC<RoleChangeModalProps> = ({ data, onClose, onSuccess }) => {
  const [impact, setImpact] = useState<RoleChangeImpact | null>(null);
  const [loadingImpact, setLoadingImpact] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [targetUsers, setTargetUsers] = useState<UserDto[]>([]);

  const [leadHandling, setLeadHandling] = useState<RoleChangeLeadHandling>(RoleChangeLeadHandling.Reassign);
  const [clientHandling, setClientHandling] = useState<RoleChangeClientHandling>(RoleChangeClientHandling.Transfer);
  const [secretaryConversion, setSecretaryConversion] = useState<SecretaryConversionMode>(SecretaryConversionMode.All);
  const [targetUserId, setTargetUserId] = useState<number | ''>('');

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showTargetError, setShowTargetError] = useState(false);

  const isOpen = !!data;

  const loadImpact = useCallback(async () => {
    if (!data) return;
    setLoadingImpact(true);
    setLoadError('');
    setImpact(null);
    setTargetUsers([]);
    setTargetUserId('');
    setSubmitError('');
    setShowTargetError(false);
    // reset choices to defaults
    setLeadHandling(RoleChangeLeadHandling.Reassign);
    setClientHandling(RoleChangeClientHandling.Transfer);
    setSecretaryConversion(SecretaryConversionMode.All);

    try {
      const result = await usersApi.getRoleChangeImpact(data.userId, data.newLevel);
      setImpact(result);

      // Load target users list only when relevant
      if (result.affectedCategory === RoleChangeAffectedCategory.Leads) {
        const reps = await usersApi.getSalesReps();
        setTargetUsers(reps.filter(u => u.id !== data.userId && u.isActive));
      } else if (result.affectedCategory === RoleChangeAffectedCategory.Clients) {
        const managers = await usersApi.getCaseManagers();
        setTargetUsers(managers.filter(u => u.id !== data.userId && u.isActive));
      }
    } catch (err: any) {
      console.error('Error loading role change impact:', err);
      setLoadError(err.response?.data?.message || 'שגיאה בטעינת פרטי ההשפעה');
    } finally {
      setLoadingImpact(false);
    }
  }, [data]);

  useEffect(() => {
    if (data) {
      loadImpact();
    }
  }, [data, loadImpact]);

  if (!isOpen || !data) return null;

  const category = impact?.affectedCategory ?? RoleChangeAffectedCategory.None;
  const needsTarget =
    (category === RoleChangeAffectedCategory.Leads && (impact?.leadCount ?? 0) > 0 && leadHandling === RoleChangeLeadHandling.Reassign) ||
    (category === RoleChangeAffectedCategory.Clients && (impact?.clientCount ?? 0) > 0 && clientHandling === RoleChangeClientHandling.Transfer);
  const targetMissing = needsTarget && targetUserId === '';

  const handleConfirm = async () => {
    if (!impact) return;
    if (targetMissing) {
      setShowTargetError(true);
      return;
    }

    const payload: ChangePermissionLevelPayload = {
      newPermissionLevel: data.newLevel,
      isSupervisor: data.isSupervisor,
      leadHandling,
      targetSalesRepId:
        category === RoleChangeAffectedCategory.Leads && impact.leadCount > 0 && leadHandling === RoleChangeLeadHandling.Reassign
          ? Number(targetUserId)
          : null,
      clientHandling,
      targetCaseManagerId:
        category === RoleChangeAffectedCategory.Clients && impact.clientCount > 0 && clientHandling === RoleChangeClientHandling.Transfer
          ? Number(targetUserId)
          : null,
      secretaryConversion,
    };

    try {
      setSubmitting(true);
      setSubmitError('');
      await usersApi.changePermissionLevel(data.userId, payload);
      onSuccess(`התפקיד של ${data.userName} שונה ל"${getRoleName(data.newLevel)}". המשתמש יצטרך להתחבר מחדש.`);
    } catch (err: any) {
      console.error('Error changing permission level:', err);
      setSubmitError(err.response?.data?.message || 'שגיאה בשינוי התפקיד');
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (loadingImpact) return <Centered>טוען פרטי השפעה…</Centered>;
    if (loadError) return <InlineError>{loadError}</InlineError>;
    if (!impact) return null;

    return (
      <>
        <Summary>
          <span>שינוי תפקיד עבור <strong>{data.userName}</strong>:</span>
          <RoleBadge $variant="old">{getRoleName(data.oldLevel)}</RoleBadge>
          <span>←</span>
          <RoleBadge $variant="new">{getRoleName(data.newLevel)}</RoleBadge>
        </Summary>

        {/* נציג מכירות יוצא — טיפול בלידים */}
        {category === RoleChangeAffectedCategory.Leads && (
          impact.leadCount === 0 ? (
            <NoteBox $type="info">
              <Info size={16} />
              <span>אין לידים משויכים לנציג זה. ניתן לשנות את התפקיד ללא השפעה נוספת.</span>
            </NoteBox>
          ) : (
            <>
              <SectionTitle>לנציג זה משויכים {impact.leadCount} לידים. כיצד לטפל בהם?</SectionTitle>
              <Options>
                <OptionLabel $checked={leadHandling === RoleChangeLeadHandling.Reassign}>
                  <input
                    type="radio"
                    name="leadHandling"
                    checked={leadHandling === RoleChangeLeadHandling.Reassign}
                    onChange={() => { setLeadHandling(RoleChangeLeadHandling.Reassign); setShowTargetError(false); }}
                  />
                  <OptionText>העברה לנציג מכירות אחר</OptionText>
                </OptionLabel>
                {leadHandling === RoleChangeLeadHandling.Reassign && (
                  <Select
                    className={showTargetError && targetMissing ? 'error' : ''}
                    value={targetUserId}
                    onChange={(e) => { setTargetUserId(e.target.value ? Number(e.target.value) : ''); setShowTargetError(false); }}
                  >
                    <option value="">בחר/י נציג מכירות…</option>
                    {targetUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.fullName}</option>
                    ))}
                  </Select>
                )}
                <OptionLabel $checked={leadHandling === RoleChangeLeadHandling.Unassign}>
                  <input
                    type="radio"
                    name="leadHandling"
                    checked={leadHandling === RoleChangeLeadHandling.Unassign}
                    onChange={() => { setLeadHandling(RoleChangeLeadHandling.Unassign); setShowTargetError(false); }}
                  />
                  <OptionText>ביטול שיוך — הלידים יחזרו למאגר הלא-משויכים</OptionText>
                </OptionLabel>
              </Options>
              {showTargetError && targetMissing && (
                <InlineError>יש לבחור נציג מכירות לקבלת הלידים, או לבחור באפשרות ביטול שיוך.</InlineError>
              )}
            </>
          )
        )}

        {/* מנהל תיקים יוצא — טיפול בלקוחות */}
        {category === RoleChangeAffectedCategory.Clients && (
          impact.clientCount === 0 ? (
            <NoteBox $type="info">
              <Info size={16} />
              <span>אין לקוחות משויכים למנהל תיק זה. ניתן לשנות את התפקיד ללא השפעה נוספת.</span>
            </NoteBox>
          ) : (
            <>
              <SectionTitle>למנהל תיק זה משויכים {impact.clientCount} לקוחות. כיצד לטפל בהם?</SectionTitle>
              <Options>
                <OptionLabel $checked={clientHandling === RoleChangeClientHandling.Transfer}>
                  <input
                    type="radio"
                    name="clientHandling"
                    checked={clientHandling === RoleChangeClientHandling.Transfer}
                    onChange={() => { setClientHandling(RoleChangeClientHandling.Transfer); setShowTargetError(false); }}
                  />
                  <OptionText>העברה למנהל תיק אחר</OptionText>
                </OptionLabel>
                {clientHandling === RoleChangeClientHandling.Transfer && (
                  <Select
                    className={showTargetError && targetMissing ? 'error' : ''}
                    value={targetUserId}
                    onChange={(e) => { setTargetUserId(e.target.value ? Number(e.target.value) : ''); setShowTargetError(false); }}
                  >
                    <option value="">בחר/י מנהל תיק…</option>
                    {targetUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.fullName}</option>
                    ))}
                  </Select>
                )}
                <OptionLabel $checked={clientHandling === RoleChangeClientHandling.Unassign}>
                  <input
                    type="radio"
                    name="clientHandling"
                    checked={clientHandling === RoleChangeClientHandling.Unassign}
                    onChange={() => { setClientHandling(RoleChangeClientHandling.Unassign); setShowTargetError(false); }}
                  />
                  <OptionText>ביטול שיוך — הלקוחות יישארו ללא מנהל תיק</OptionText>
                </OptionLabel>
              </Options>
              {showTargetError && targetMissing && (
                <InlineError>יש לבחור מנהל תיק לקבלת הלקוחות, או לבחור באפשרות ביטול שיוך.</InlineError>
              )}
            </>
          )
        )}

        {/* מזכירה יוצאת — המרת קישורים */}
        {category === RoleChangeAffectedCategory.SecretaryLinks && (
          <>
            {/* מזכירה ← מנהל תיקים */}
            {data.newLevel === 3 && (
              impact.secretaryClientCount === 0 ? (
                <NoteBox $type="info">
                  <Info size={16} />
                  <span>אין לקוחות המשויכים למזכירה זו להמרה.</span>
                </NoteBox>
              ) : (
                <>
                  <SectionTitle>למזכירה משויכים {impact.secretaryClientCount} לקוחות. מה לעשות איתם?</SectionTitle>
                  <Options>
                    <OptionLabel $checked={secretaryConversion === SecretaryConversionMode.All}>
                      <input type="radio" name="secConv" checked={secretaryConversion === SecretaryConversionMode.All}
                        onChange={() => setSecretaryConversion(SecretaryConversionMode.All)} />
                      <OptionText>להפוך אותה למנהלת התיק של כל {impact.secretaryClientCount} הלקוחות</OptionText>
                    </OptionLabel>
                    <OptionLabel $checked={secretaryConversion === SecretaryConversionMode.OnlyUnassigned}>
                      <input type="radio" name="secConv" checked={secretaryConversion === SecretaryConversionMode.OnlyUnassigned}
                        onChange={() => setSecretaryConversion(SecretaryConversionMode.OnlyUnassigned)} />
                      <OptionText>רק לקוחות שאין להם מנהל תיק כעת ({impact.clientsWithoutManagerCount})</OptionText>
                    </OptionLabel>
                    <OptionLabel $checked={secretaryConversion === SecretaryConversionMode.None}>
                      <input type="radio" name="secConv" checked={secretaryConversion === SecretaryConversionMode.None}
                        onChange={() => setSecretaryConversion(SecretaryConversionMode.None)} />
                      <OptionText>לא לשייך אליה לקוחות כלל</OptionText>
                    </OptionLabel>
                  </Options>
                </>
              )
            )}

            {/* מזכירה ← נציג מכירות (התנהגות מראה) */}
            {data.newLevel === 2 && (
              impact.secretaryLeadCount === 0 ? (
                <NoteBox $type="info">
                  <Info size={16} />
                  <span>אין לידים המשויכים למזכירה זו להמרה.</span>
                </NoteBox>
              ) : (
                <>
                  <SectionTitle>למזכירה משויכים {impact.secretaryLeadCount} לידים. מה לעשות איתם?</SectionTitle>
                  <Options>
                    <OptionLabel $checked={secretaryConversion === SecretaryConversionMode.All}>
                      <input type="radio" name="secConv" checked={secretaryConversion === SecretaryConversionMode.All}
                        onChange={() => setSecretaryConversion(SecretaryConversionMode.All)} />
                      <OptionText>להפוך אותה לנציגת המכירות של כל {impact.secretaryLeadCount} הלידים</OptionText>
                    </OptionLabel>
                    <OptionLabel $checked={secretaryConversion === SecretaryConversionMode.OnlyUnassigned}>
                      <input type="radio" name="secConv" checked={secretaryConversion === SecretaryConversionMode.OnlyUnassigned}
                        onChange={() => setSecretaryConversion(SecretaryConversionMode.OnlyUnassigned)} />
                      <OptionText>רק לידים שאינם משויכים כעת ({impact.unassignedLeadCount})</OptionText>
                    </OptionLabel>
                    <OptionLabel $checked={secretaryConversion === SecretaryConversionMode.None}>
                      <input type="radio" name="secConv" checked={secretaryConversion === SecretaryConversionMode.None}
                        onChange={() => setSecretaryConversion(SecretaryConversionMode.None)} />
                      <OptionText>לא לשייך אליה לידים כלל</OptionText>
                    </OptionLabel>
                  </Options>
                </>
              )
            )}

            {/* הערה על הסרת קישורי המזכירה הנותרים */}
            <NoteBox $type="warning">
              <AlertTriangle size={16} />
              <span>
                {data.newLevel === 3 && impact.secretaryLeadCount > 0 &&
                  `קישורי המזכירה ל-${impact.secretaryLeadCount} לידים יוסרו. `}
                {data.newLevel === 2 && impact.secretaryClientCount > 0 &&
                  `קישורי המזכירה ל-${impact.secretaryClientCount} לקוחות יוסרו. `}
                {data.newLevel === 1 &&
                  `כל קישורי המזכירה (${impact.secretaryLeadCount} לידים, ${impact.secretaryClientCount} לקוחות) יוסרו. `}
                המשתמש לא יהיה עוד מזכירה.
              </span>
            </NoteBox>
          </>
        )}

        {category === RoleChangeAffectedCategory.None && (
          <NoteBox $type="info">
            <Info size={16} />
            <span>אין ישויות המשויכות למשתמש בתפקיד הנוכחי. ניתן לשנות את התפקיד ללא השפעה נוספת.</span>
          </NoteBox>
        )}

        <NoteBox $type="info">
          <Info size={16} />
          <span>המשתמש יצטרך להתחבר מחדש כדי שהשינוי ייכנס לתוקף.</span>
        </NoteBox>

        {submitError && <InlineError>{submitError}</InlineError>}
      </>
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => { if (!submitting) onClose(); }}
      title="שינוי תפקיד משתמש"
      width="520px"
      closable={!submitting}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            ביטול
          </Button>
          <Button
            variant="primary"
            onClick={handleConfirm}
            disabled={submitting || loadingImpact || !!loadError}
          >
            {submitting ? 'מבצע…' : 'שנה תפקיד'}
          </Button>
        </>
      }
    >
      {renderBody()}
    </Modal>
  );
};

export default RoleChangeModal;
